import Axios from 'axios';
import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import {
  amperLogIn,
  amperLogOut,
  amperChangePassword,
  amperRefreshToken,
  amperChangeUserData,
  amperRegister,
} from 'app/utils/amperAuth';
import { addAxiosInterceptors } from 'app/utils/axiosInterceptors';
import {
  getLocalRefreshToken,
  setLocalRefershToken,
} from 'app/utils/refreshToken';
import { useTrackedRef } from 'app/utils/trackedRef';
import { RegisterModalContext } from 'app/contexts/RegisterModalContext';

export const useAmperAuth = (onAuthChange: (isLoggedIn: boolean) => void) => {
  const [isReady, setIsReady] = React.useState(false);
  const token = React.useRef('');
  const refreshToken = useTrackedRef<undefined | string>(undefined);
  const [loading, setLoading] = React.useState(false);
  const history = useHistory();
  const { openLoginModal } = useContext(RegisterModalContext);

  const register = amperRegister;

  const changeUserData = amperChangeUserData;

  const changePassword = amperChangePassword;

  const login = (username: string, password: string) => {
    return amperLogIn(username, password).then(({ data, error }) => {
      if (error) {
        return error;
      }
      token.current = data.access;
      refreshToken.current = data.refresh;
      changeAuth();
      if (data.access) {
        return undefined;
      }
    });
  };

  const changeAuth = () => {
    if (refreshToken.current && refreshToken.current.length > 0) {
      onAuthChange(true);
    } else {
      onAuthChange(false);
    }
  };

  const logout = () => {
    const currentQuery = new URLSearchParams(window.location.search).toString(); // Get existing query parameters

    if (refreshToken.current) {
      amperLogOut(refreshToken.current).finally(() => {
        token.current = '';
      });
      refreshToken.current = '';
    }

    changeAuth();

    history.push('/')
  };


  const tryTokenRefresh = async (refresh_token: string) => {
    return amperRefreshToken(refresh_token).then(data => {
      token.current = data.access;
      refreshToken.current = data.refresh;
      if (data.access) return true;
      history.push('/')
      setLoading(false);
      return false;
    });
  };

  React.useEffect(() => {
    // On component load, retrieve refresh token from local storage,
    // if a refresh token exists use it to fetch access token
    const retoken = getLocalRefreshToken();
    if (retoken) {
      setLoading(true);
      refreshToken.current = retoken;
      tryTokenRefresh(retoken).finally(() => {
        changeAuth();
      });
    }

    const refTokenListener = refreshToken.addListener(reftoken => {
      if (reftoken !== undefined) {
        // whenever the refresh token changes, save it to the local storage
        setLocalRefershToken(reftoken);
      }
    });

    // add interceptors for handling token refreshes and adding tokens to the requests
    const [requestInterceptor, responseInterceptor] = addAxiosInterceptors({
      token,
      refreshToken,
      onRequestError: logout,
      tokenRefreshMethod: tryTokenRefresh,
    });
    setIsReady(true);

    return () => {
      Axios.interceptors.request.eject(requestInterceptor);
      Axios.interceptors.response.eject(responseInterceptor);
      refreshToken.removeListener(refTokenListener);
    };
  }, []);

  return {
    login,
    logout,
    changePassword,
    changeUserData,
    isReady,
    loading,
    setLoading,
    register,
  };
};
